import { INPUT_QUALITY_TIERS, INPUT_QUALITY_TIER_INDEX } from '../../utils/meetingInputQuality'

const TIER_LABELS = Object.freeze({
  weak: 'Weak',
  medium: 'Medium',
  strong: 'Strong',
  excellent: 'Excellent',
})

const TIER_SEGMENT_ACTIVE = Object.freeze({
  weak: 'bg-amber-400',
  medium: 'bg-amber-500',
  strong: 'bg-emerald-500',
  excellent: 'bg-emerald-600',
})

const TIER_BADGE = Object.freeze({
  weak: 'bg-amber-50 text-amber-800 ring-amber-200/80',
  medium: 'bg-amber-50 text-amber-900 ring-amber-200/80',
  strong: 'bg-emerald-50 text-emerald-800 ring-emerald-200/80',
  excellent: 'bg-emerald-100 text-emerald-900 ring-emerald-300/70',
})

/**
 * Compact quality meter for meeting builder title and agenda inputs.
 * @param {{ tier: string | null, hint: string, label?: string, compact?: boolean }} props
 */
export default function MeetingInputQualityIndicator({ tier, hint, label = 'Input quality', compact = false }) {
  if (!tier) return null

  const activeIndex = INPUT_QUALITY_TIER_INDEX[tier] ?? 0

  return (
    <div
      className={`rounded-2xl border border-gray-100 bg-gray-50/90 ${compact ? 'px-3 py-2' : 'px-3.5 py-2.5 sm:px-4 sm:py-3'}`}
      role="status"
      aria-live="polite"
      aria-label={`${label}: ${TIER_LABELS[tier] ?? tier}`}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-gray-500 sm:text-xs">
          {label}
        </span>
        <div className="flex min-w-[7rem] max-w-[12rem] flex-1 items-center gap-1" aria-hidden>
          {INPUT_QUALITY_TIERS.map((step, index) => {
            const filled = index <= activeIndex
            const isCurrent = index === activeIndex
            return (
              <div
                key={step}
                className={`h-1.5 flex-1 rounded-full transition-colors duration-200 ${
                  filled
                    ? isCurrent
                      ? TIER_SEGMENT_ACTIVE[tier]
                      : index < activeIndex
                        ? 'bg-emerald-300/90'
                        : 'bg-emerald-400/80'
                    : 'bg-gray-200'
                }`}
              />
            )
          })}
        </div>
        <span
          className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[0.65rem] font-semibold ring-1 sm:text-xs ${TIER_BADGE[tier] ?? TIER_BADGE.weak}`}
        >
          {TIER_LABELS[tier] ?? tier}
        </span>
      </div>
      {hint ? (
        <p
          className={`mt-1.5 text-gray-500 ${compact ? 'text-[0.7rem] leading-snug' : 'text-xs leading-snug sm:text-sm'}`}
        >
          {hint}
        </p>
      ) : null}
    </div>
  )
}
